import {useEffect, useState} from "react";
import CircleBadge from "./CircleBadge";
import BenefitSimplePrizeUsers from "./project/BenefitSimplePrizeUsers";
import SuspensePopup from "../SuspensePopup";
import EChartsReact from "echarts-for-react";
import {basicBarOption} from "../../constants/chart_config";

const BenefitStatisticsDetail = ({projectId, benefit, events, index}) => {
    const [showPrizeUsers, setShowPrizeUsers] = useState(false)
    const [prizeBenefitId, setPrizeBenefitId] = useState()

    const [entryPointUsedRate, setEntryPointUsedRate] = useState({})

    useEffect(() => {
        const entryPointQuantities = events.flatMap((event) => event.entryPointQuantities)
        setEntryPointUsedRate({
            /* 🔴 `detached` = 지금은 이 프로젝트에 연결돼 있지 않지만 **발급 이력이 있는** 진입점
               (2026-09-04. 성과 축을 "현재 연결 ∪ 발급 이력" 으로 넓혔다).
               라벨로 구분하지 않으면 담당자가 "왜 안 쓰는 지점이 여기 있지?" 로 혼란스럽다. */
            xAxis: entryPointQuantities.map(entryPoint =>
                entryPoint.detached ? `${entryPoint.name} (연결 해제됨)` : entryPoint.name),
            series: entryPointQuantities.map(entryPoint => {
                return {
                    value: entryPoint.usedRate,
                    extraInfo: ` (${entryPoint.usedQuantity} / ${entryPoint.issuedQuantity})`
                }
            })
        })
    }, [events])

    // 링크 혜택형은 외부 URL 로 나가므로 시스템이 사용 시점을 알 수 없다 → '사용률 0%' 로 오해하지 않게 별도 표시.
    // (근거: 혜택 페이지가 window.open 만 하고 사용 처리 API 를 호출하지 않아 is_used 가 영구히 false)
    const usedTrackable = benefit.isUsedTrackable !== false

    return (
        <>
            <div className="flex-col gap-4">
                <div className="flex justify-between" style={{paddingRight: "20px"}}>
                    <div className="flex items-center gap-2">
                        <CircleBadge label={index + 1} />
                        <div className="text-primary">[{benefit.typeName}]</div>
                        <div className="font-bold">{benefit.name}</div>
                        {/* 🔴 `detached` = 지금은 이 프로젝트의 어떤 이벤트에도 없지만 **발급 이력이 있는** 혜택
                            (2026-09-23. 혜택 축을 "현재 이벤트 ∪ 발급 이력" 으로 넓혔다 — 이벤트를 프로젝트에서 빼도
                            그 혜택의 성과가 사라지지 않게). 설정 확률이 없으므로 `(%)` 대신 사유를 보여준다. */}
                        {benefit.detached
                            ? <div className="text-tertiary text-[12px]">연결 해제된 이벤트의 혜택 · 발급 이력만 표시</div>
                            : <div className="font-bold">({benefit.winningPercent}%)</div>}
                    </div>
                </div>
                <div className="flex justify-between border">
                    <div className="flex-align-center w-full justify-between" style={{padding: "10px 10px", borderRight: "1px solid var(--grey-1)"}}>
                        <div>원가</div>
                        <div className="font-bold">{benefit.supplyPrice.toLocaleString()}</div>
                    </div>
                    <div className="flex-align-center w-full justify-between" style={{padding: "10px 10px", borderRight: "1px solid var(--grey-1)"}}>
                        <div>소비자가</div>
                        <div className="font-bold">{benefit.retailPrice?.toLocaleString() ?? "미설정"}</div>
                    </div>
                    <div className="flex-align-center w-full justify-between" style={{padding: "10px 10px", borderRight: "1px solid var(--grey-1)"}}>
                        <div>{usedTrackable ? "사용/발급(사용률)" : "발급"}</div>
                        {
                            usedTrackable ? (
                                <div className="flex gap-1 font-bold">
                                    <div className="text-primary">{benefit.usedQuantity?.toLocaleString()}</div>
                                    <div>/</div>
                                    <div>{benefit.issuedQuantity?.toLocaleString()}</div>
                                    <div>({benefit.usedRate}%)</div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="font-bold">{benefit.issuedQuantity?.toLocaleString()}</div>
                                    <div className="text-tertiary text-[12px]">사용 확인 불가</div>
                                </div>
                            )
                        }
                    </div>
                    <div className="flex-align-center w-full justify-between" style={{padding: "10px 10px"}}>
                        <div>당첨자 확인</div>
                        <div className="font-bold underline pointer"
                             onClick={() => {
                                 setPrizeBenefitId(benefit.id)
                                 setShowPrizeUsers(true)
                             }}>당첨자 조회</div>
                    </div>
                </div>
                {
                    usedTrackable ? (
                        <EChartsReact
                            option={basicBarOption(entryPointUsedRate.xAxis, entryPointUsedRate.series, "%")}
                        />
                    ) : (
                        // 사용률 차트는 전부 0 으로만 그려져 오해를 부른다 → 사유를 안내한다.
                        <div className="bg-depth1 text-tertiary text-[13px]" style={{padding: "12px"}}>
                            링크 혜택형은 고객이 외부 링크로 이동하므로 <b>실제 사용 여부를 확인할 수 없습니다</b>.
                            발급 수와 클릭 수로만 성과를 판단해 주세요.
                        </div>
                    )
                }
            </div>
            <SuspensePopup
                title={benefit.name}
                visible={showPrizeUsers}
                style={{width: "1100px"}}
                onClose={() => setShowPrizeUsers(false)}
            >
                <BenefitSimplePrizeUsers projectId={projectId} benefitId={prizeBenefitId} />
            </SuspensePopup>
        </>
    )
}

export default BenefitStatisticsDetail